import { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import some from 'lodash/some';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { green } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import flattenObject from '../../utils/flatten-object';

const useStyles = makeStyles((theme) => ({
  success: {
    backgroundColor: green[600],
  },
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  close: {
    padding: theme.spacing(0.5)
  },
  icon: {
    fontSize: 20,
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const getErrorType = (props) => {
  const { errors, status } = props;
  if (status) {
    if (status.networkError) {
      return 'networkError';
    }
    if (status.graphQLErrors && status.graphQLErrors.length) {
      return 'graphQLErrors';
    }
  }
  if (some(flattenObject(errors), (v) => v)) {
    return 'formikErrors';
  }
  return null;
};

const FormSubmitStatus = (props) => {
  const { isSubmitting, submitCount, successMessage, autoHideDuration } = props;
  const classes = useStyles();
  const [status, setStatus] = useState(null);
  const [currentSubmitCount, setCurrentSubmitCount] = useState(submitCount);

  useEffect(() => {
    if (!isSubmitting && submitCount !== currentSubmitCount) {
      setCurrentSubmitCount(submitCount);
      setStatus(getErrorType(props) || 'success');
    }
  }, [isSubmitting, submitCount]);

  function handleClose() {
    setStatus(null);
  }

  return (
    <Fragment>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={status === "networkError" || status === 'graphQLErrors' || status === 'formikErrors'}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
      >
        <SnackbarContent
          className={classes.error}
          aria-describedby="snackbar-message"
          message={
            <span id="snackbar-message" className={classes.message}>
              <ErrorIcon className={clsx(classes.icon, classes.iconVariant)} />
              {status === 'formikErrors' && (
                'There were errors with your submission'
              )}
              {status === 'graphQLErrors' && (
                'There were errors with your submission'
              )}
              {status === 'networkError' && (
                'A network error occurred'
              )}
            </span>
          }
          action={[
            <IconButton key="close" aria-label="close" color="inherit" onClick={handleClose}>
              <CloseIcon className={classes.icon} />
            </IconButton>
          ]}
        />
      </Snackbar>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={status === 'success'}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <SnackbarContent
          className={classes.success}
          aria-describedby="snackbar-message"
          message={
            <span id="snackbar-message" className={classes.message}>
              <CheckCircleIcon className={clsx(classes.icon, classes.iconVariant)} />
              {successMessage}
            </span>
          }
          action={[
            <IconButton key="close" aria-label="close" color="inherit" onClick={handleClose}>
              <CloseIcon className={classes.icon} />
            </IconButton>
          ]}
        />
      </Snackbar>
    </Fragment>
  );
};

FormSubmitStatus.propTypes = {
  isSubmitting: PropTypes.bool,
  submitCount: PropTypes.number,
  errors: PropTypes.object,
  status: PropTypes.object,
  successMessage: PropTypes.node,
  autoHideDuration: PropTypes.number,
};

FormSubmitStatus.defaultProps = {
  successMessage: 'Saved!',
  autoHideDuration: 3000,
};

export default FormSubmitStatus;
